// File: src/components/admin/sync/SyncListView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschrankt)
// All rights reserved.
//
// Issue #233: Admin sync observability — list view with filters.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, MenuItem, Grid,
    CircularProgress, Stack, Alert, Tooltip, FormControlLabel, Switch,
    TablePagination,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    GetApp as ExportIcon,
    Visibility as ViewIcon,
    Sync as SyncIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const STATUS_CONFIG = {
    completed: { color: 'success', label: 'Completed' },
    partial: { color: 'warning', label: 'Partial' },
    failed: { color: 'error', label: 'Failed' },
    in_progress: { color: 'info', label: 'In Progress' },
    cancelled: { color: 'default', label: 'Cancelled' },
};

const formatDuration = (ms) => {
    if (!ms && ms !== 0) return '-';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainSec = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainSec}s`;
    const hours = Math.floor(minutes / 60);
    const remainMin = minutes % 60;
    return `${hours}h ${remainMin}m`;
};

const formatRelativeTime = (isoString) => {
    if (!isoString) return '-';
    const diff = Date.now() - new Date(isoString).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const formatEntities = (totals) => {
    if (!totals) return '-';
    const parts = [];
    if (totals.adGroups) parts.push(`${totals.adGroups} AG`);
    if (totals.keywords) parts.push(`${totals.keywords} KW`);
    if (totals.ads) parts.push(`${totals.ads} Ads`);
    if (totals.conversionActions) parts.push(`${totals.conversionActions} Conv`);
    return parts.length > 0 ? parts.join(' \u00b7 ') : '-';
};

const SyncListView = () => {
    const navigate = useNavigate();
    const [syncs, setSyncs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });

    // Filters
    const [filterUid, setFilterUid] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterScheduled, setFilterScheduled] = useState('');
    const [filterPlatform, setFilterPlatform] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    // Auto-refresh
    const [autoRefresh, setAutoRefresh] = useState(false);
    const autoRefreshRef = useRef(null);

    const fetchSyncs = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);
            const params = { page, pageSize: pagination.pageSize };
            if (filterStatus) params.status = filterStatus;
            if (filterUid) params.uid = filterUid;
            if (filterPlatform) params.platform = filterPlatform;
            if (filterScheduled) params.scheduled = filterScheduled;
            if (filterStartDate) params.startDate = filterStartDate;
            if (filterEndDate) params.endDate = filterEndDate;

            const data = await adminApi.getAdminSyncs(params);
            setSyncs(data.syncs || []);
            setPagination(data.pagination || { page, pageSize: 25, total: 0, totalPages: 0 });
        } catch (err) {
            setError(handleAdminApiError(err).message);
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterUid, filterPlatform, filterScheduled, filterStartDate, filterEndDate, pagination.pageSize]);

    useEffect(() => {
        fetchSyncs(1);
    }, [fetchSyncs]);

    // Auto-refresh effect
    useEffect(() => {
        if (autoRefresh) {
            autoRefreshRef.current = setInterval(() => fetchSyncs(pagination.page), 30000);
        }
        return () => {
            if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
        };
    }, [autoRefresh, fetchSyncs, pagination.page]);

    const handlePageChange = (event, newPage) => {
        fetchSyncs(newPage + 1);
    };

    const handleRowsPerPageChange = (event) => {
        setPagination(prev => ({ ...prev, pageSize: parseInt(event.target.value) }));
    };

    const handleDownloadCSV = async (e, uid, syncId) => {
        e.stopPropagation();
        try {
            const blob = await adminApi.downloadAdminSyncCSV(uid, syncId);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `sync-${syncId}-${uid}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        }
    };

    const handleBackfill = async () => {
        try {
            setLoading(true);
            const result = await adminApi.backfillSyncIndex();
            setError(null);
            alert(`Backfill complete: ${result.backfilled} records indexed`);
            fetchSyncs(1);
        } catch (err) {
            setError(handleAdminApiError(err).message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">
                    <SyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Sync Records
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                        control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} size="small" />}
                        label="Auto-refresh"
                    />
                    <Button variant="outlined" size="small" onClick={handleBackfill} disabled={loading}>
                        Backfill Index
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => fetchSyncs(pagination.page)} disabled={loading}>
                        Refresh
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={2}>
                        <TextField
                            fullWidth size="small" label="User (UID)"
                            value={filterUid} onChange={(e) => setFilterUid(e.target.value)}
                            data-testid="filter-uid"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            select fullWidth size="small" label="Status"
                            value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                            data-testid="filter-status"
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="partial">Partial</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            select fullWidth size="small" label="Type"
                            value={filterScheduled} onChange={(e) => setFilterScheduled(e.target.value)}
                            data-testid="filter-scheduled"
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="true">Scheduled</MenuItem>
                            <MenuItem value="false">Manual</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            select fullWidth size="small" label="Platform"
                            value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}
                            data-testid="filter-platform"
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="google-ads">Google Ads</MenuItem>
                            <MenuItem value="microsoft-ads">Microsoft Ads</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth size="small" label="Start Date" type="date"
                            value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            data-testid="filter-start-date"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth size="small" label="End Date" type="date"
                            value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            data-testid="filter-end-date"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper}>
                {loading && syncs.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Table size="small" data-testid="sync-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Started</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Platform(s)</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Campaigns</TableCell>
                                    <TableCell>Entities</TableCell>
                                    <TableCell align="right">Errors</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {syncs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">No sync records found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    syncs.map((sync) => {
                                        const statusCfg = STATUS_CONFIG[sync.status] || { color: 'default', label: sync.status };
                                        const platformNames = Object.values(sync.platforms || {}).map(p => p.platformName).join(', ');
                                        return (
                                            <TableRow
                                                key={`${sync.uid}-${sync.syncId}`}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => navigate(`/syncs/${sync.uid}/${sync.syncId}`)}
                                                data-testid="sync-row"
                                            >
                                                <TableCell>
                                                    <Chip
                                                        label={statusCfg.label}
                                                        color={statusCfg.color}
                                                        size="small"
                                                        sx={{ fontSize: '0.70rem', height: 24 }}
                                                        data-testid="sync-status-chip"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={`UID: ${sync.uid}`}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/users/${sync.uid}`); }}
                                                            data-testid="sync-user-email"
                                                        >
                                                            {sync.email || sync.uid}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={sync.startedAt}>
                                                        <span>{formatRelativeTime(sync.startedAt)}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    {sync.status === 'in_progress' ? (
                                                        <Typography variant="body2" color="info.main" data-testid="sync-duration-running">
                                                            Running... {formatDuration(sync.durationMs)}
                                                        </Typography>
                                                    ) : (
                                                        formatDuration(sync.durationMs)
                                                    )}
                                                </TableCell>
                                                <TableCell>{platformNames || '-'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={sync.scheduled ? 'Scheduled' : 'Manual'}
                                                        size="small"
                                                        variant="outlined"
                                                        icon={sync.scheduled ? <ScheduleIcon /> : <PersonIcon />}
                                                        sx={{ fontSize: '0.70rem', height: 24 }}
                                                        data-testid="sync-type-chip"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{sync.totals?.campaigns || 0}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: '0.80rem' }}>
                                                        {formatEntities(sync.totals)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {sync.totals?.errors > 0 ? (
                                                        <Typography color="error" variant="body2" fontWeight="bold">
                                                            {sync.totals.errors}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">0</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="View Detail">
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/syncs/${sync.uid}/${sync.syncId}`); }} data-testid="sync-view-btn">
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download CSV">
                                                        <IconButton size="small" onClick={(e) => handleDownloadCSV(e, sync.uid, sync.syncId)} data-testid="sync-csv-btn">
                                                            <ExportIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.page - 1}
                            rowsPerPage={pagination.pageSize}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            data-testid="sync-pagination"
                        />
                    </>
                )}
            </TableContainer>
        </Box>
    );
};

export default SyncListView;
